import {
  collection,
  doc,
  documentId,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  writeBatch,
  setDoc,
  deleteDoc,
  updateDoc,
} from "firebase/firestore";
import {
  runtimeDb as db,
  requireCustomerRuntime,
  hasActiveRuntime,
  getActiveRuntimeConnectionId,
} from "../../firestoreStudio/runtime/runtimeDb";
import { BATCH_SIZE } from "../constants";
import { registerCollection, touchCollection } from "../../../services/collectionRegistryService";
import {
  adminFetchDocuments,
  adminGetCollectionCount,
} from "../../../services/customerDocumentQueryService";

async function ensureWriteRegistry(collectionName, actorId) {
  const name = String(collectionName || "").trim();
  if (!name) return;
  await registerCollection(name, { createdBy: actorId || null });
  await touchCollection(name, actorId || null);
}

function shouldUseAdminReadPath() {
  return hasActiveRuntime() && Boolean(getActiveRuntimeConnectionId());
}

async function fetchDocumentsClient({
  collectionName,
  sortField = "id",
  sortAsc = true,
  pageLimit = 100,
  cursor = null,
  docIdQuery = "",
  fieldQuery = "",
}) {
  const col = collectionName.trim();
  if (!col) return { rows: [], hasMore: false, cursor: null };

  const idQ = docIdQuery.trim();
  if (idQ) {
    const snap = await getDoc(doc(db, col, idQ));
    if (snap.exists()) {
      return {
        rows: [{ id: snap.id, data: snap.data() || {} }],
        hasMore: false,
        cursor: null,
      };
    }
  }

  const orderField = sortField === "id" ? documentId() : sortField;
  let q = query(
    collection(db, col),
    orderBy(orderField, sortAsc ? "asc" : "desc"),
    limit(pageLimit + 1)
  );
  if (cursor && cursor.id) q = query(q, startAfter(cursor));

  const snap = await getDocs(q);
  let docs = snap.docs.map((d) => ({ id: d.id, data: d.data(), _snap: d }));

  const fq = fieldQuery.trim().toLowerCase();
  if (fq) {
    docs = docs.filter((d) => {
      const blob = JSON.stringify(d.data).toLowerCase();
      return d.id.toLowerCase().includes(fq) || blob.includes(fq);
    });
  }

  const more = docs.length > pageLimit;
  if (more) docs = docs.slice(0, pageLimit);

  return {
    rows: docs.map(({ id, data }) => ({ id, data })),
    hasMore: more,
    cursor: docs.length ? docs[docs.length - 1]._snap : cursor,
    rawDocs: docs,
  };
}

export async function getCollectionCount(collectionName) {
  requireCustomerRuntime();
  if (shouldUseAdminReadPath()) {
    return adminGetCollectionCount(collectionName);
  }
  const snap = await getCountFromServer(collection(db, collectionName));
  return snap.data().count;
}

export async function fetchDocuments({
  collectionName,
  sortField = "id",
  sortAsc = true,
  pageLimit = 100,
  cursor = null,
  docIdQuery = "",
  fieldQuery = "",
  actorId = null,
}) {
  requireCustomerRuntime();
  const col = collectionName.trim();
  if (!col) return { rows: [], hasMore: false, cursor: null };

  if (shouldUseAdminReadPath()) {
    return adminFetchDocuments({
      collectionName: col,
      sortField,
      sortAsc,
      pageLimit,
      cursor,
      docIdQuery,
      fieldQuery,
    });
  }

  return fetchDocumentsClient({
    collectionName: col,
    sortField,
    sortAsc,
    pageLimit,
    cursor,
    docIdQuery,
    fieldQuery,
    actorId,
  });
}

export async function commitBatches(operations) {
  let batch = writeBatch(db);
  let count = 0;
  const commits = [];

  for (const op of operations) {
    if (op.type === "set") batch.set(op.ref, op.data, op.options);
    else if (op.type === "update") batch.update(op.ref, op.data);
    else if (op.type === "delete") batch.delete(op.ref);

    count += 1;
    if (count >= BATCH_SIZE) {
      commits.push(batch.commit());
      batch = writeBatch(db);
      count = 0;
    }
  }
  if (count > 0) commits.push(batch.commit());
  await Promise.all(commits);
}

export async function bulkDeleteDocuments(collectionName, ids, actorId = null) {
  await ensureWriteRegistry(collectionName, actorId);
  const operations = ids.map((id) => ({
    type: "delete",
    ref: doc(db, collectionName, id),
  }));
  await commitBatches(operations);
}

export async function bulkUpdateDocuments(collectionName, updates, actorId = null) {
  await ensureWriteRegistry(collectionName, actorId);
  const operations = updates.map(({ id, data }) => ({
    type: "update",
    ref: doc(db, collectionName, id),
    data,
  }));
  await commitBatches(operations);
}

export async function updateDocument(collectionName, docId, data, actorId = null) {
  await ensureWriteRegistry(collectionName, actorId);
  await updateDoc(doc(db, collectionName, docId), data);
}

export async function setDocument(collectionName, docId, data, actorId = null) {
  await ensureWriteRegistry(collectionName, actorId);
  await setDoc(doc(db, collectionName, docId), data);
}

export async function deleteDocument(collectionName, docId, actorId = null) {
  await ensureWriteRegistry(collectionName, actorId);
  await deleteDoc(doc(db, collectionName, docId));
}

export async function fetchDocumentsByIds(collectionName, ids) {
  const out = {};
  for (const id of ids) {
    const snap = await getDoc(doc(db, collectionName, id));
    if (snap.exists()) out[id] = snap.data();
  }
  return out;
}
